<script setup>
import Artifact from '@/components/artifacts/ArtifactDisplay.vue';
import { computed, ref, watch } from 'vue';
import { useArtifactStore } from '@/store/artifacts';
import { useDisplay, useTheme } from 'vuetify';
import { useMiscellaneousStore } from '@/store/miscellaneous';
import MiniArtifact from '@/components/artifacts/MiniArtifactDisplay.vue';

const props = defineProps({
  artifacts: { type: Array, default: () => [] },
  listTitle : { type: String, required: false, default: null },
  isSideBar: { type: Boolean, default: false },
  containerClass: { type: String, required: false, default: '' },
})

const miscellaneousStore = useMiscellaneousStore()
const artifactStore = useArtifactStore()
const { mobile } = useDisplay()
const theme = useTheme()

const viewMode = ref(miscellaneousStore.artifactListDisplayMode)
const downloadFinished = ref(0)
const downloadFailed = ref(0)
const downloadTotal = ref(0)
const currentlyDownloading = ref(false)

const downloadProgress = computed(() => downloadFinished.value + downloadFailed.value)

async function downloadAll() {
  const promises = []
  downloadFinished.value = 0
  downloadFailed.value = 0
  currentlyDownloading.value = true
  downloadTotal.value = props.artifacts.length
  for (let artifact of props.artifacts) {
    promises.push(artifactStore.downloadArtifactPayload(artifact.artifactId)
      .then(() => downloadFinished.value++)
      .catch(() => downloadFailed.value++))
  }
  await Promise.all(promises)
  currentlyDownloading.value = false
}

async function downloadAndSaveAll() {
  const promises = []
  downloadFinished.value = 0
  downloadFailed.value = 0
  currentlyDownloading.value = true
  downloadTotal.value = props.artifacts.length
  for(let artifact of props.artifacts) {
    promises.push(
      artifactStore.downloadAndSaveArtifactPayload(artifact.artifactId)
        .then(successful => {if (successful) downloadFinished.value++; else downloadFailed.value++})
        .catch(),
    )
  }
  await Promise.all(promises)
  currentlyDownloading.value = false
}

watch(viewMode, () => {
  miscellaneousStore.artifactListDisplayMode = viewMode.value
})
</script>

<template>
  <v-container
    fluid
    style="margin: 16px 0; padding: 0;"
    :class="props.containerClass"
  >
    <!-- download & save & grid / list buttons -->
    <div
      class="mt-8 mb-4 d-flex flex-row align-center ga-4 flex-wrap"
      :class="
        { 
          'justify-end': !props.listTitle,
          'justify-space-between': props.listTitle
        }
      "
    >
      <v-card-title
        v-if="props.listTitle"
        style="max-width: 100%"
      >
        {{ props.listTitle }}
      </v-card-title>

      <div class="d-flex flex-row align-center ga-4 flex-wrap">
        <v-btn
          prepend-icon="mdi-download"
          :color="theme.current.value.colors.surface"
          height="40"
          icon
          @click="downloadAll"
        >
          <v-icon icon="mdi-download-multiple" />
          <v-tooltip
            activator="parent"
            location="top"
          >
            {{ $t("general_interface.buttons.download_all") }}
          </v-tooltip>
        </v-btn>
        
        <v-btn
          prepend-icon="mdi-content-save"
          :color="theme.current.value.colors.surface"
          height="40"
          icon
          @click="downloadAndSaveAll"
        >
          <v-icon icon="mdi-content-save-all" />
          <v-tooltip
            activator="parent"
            location="top"
          >
            {{ $t("general_interface.buttons.save_all") }}
          </v-tooltip>
        </v-btn>

        <div v-if="!mobile && !isSideBar">
          <v-btn-toggle
            v-model="viewMode"
            :mandatory="true"
            density="comfortable"
            divided
            elevation="2"
          >
            <v-btn
              class="rounded-0"
              value="list"
              color="primary"
              height="40"
              :base-color="theme.current.value.colors.surface"
            >
              <v-icon
                icon="mdi-view-list"
                size="24"
              />
              <v-tooltip
                activator="parent"
                location="top"
              >
                {{ $t("general_interface.buttons.list") }}
              </v-tooltip>
            </v-btn>
            <v-btn
              class="rounded-0"
              value="table"
              color="primary"
              height="40"
              :base-color="theme.current.value.colors.surface"
            >
              <v-icon
                icon="mdi-view-grid"
                size="24"
              />
              <v-tooltip
                activator="parent"
                location="top"
              >
                {{ $t("general_interface.buttons.grid") }}
              </v-tooltip>
            </v-btn>
          </v-btn-toggle>
        </div>
      </div>
    </div>
    
    <v-col
      v-if="currentlyDownloading"
      :cols="12"
      class="px-4"
    >
      <v-progress-linear
        height="20"
        rounded
        :max="downloadTotal"
        :model-value="downloadProgress"
        color="primary"
      >
        <template #default="{value}">
          <strong>
            {{ Math.ceil(value).toString()+"%" }}
          </strong>
        </template>
      </v-progress-linear>
    </v-col>

    <v-divider class="mb-4" />

    <v-row>
      <v-col
        v-for="artifact of artifacts"
        :key="artifact.artifactId"
        :cols="viewMode === 'table' ? 4 && !mobile : 12"
        :min-width="100"
        :min-height="100"
      >
        <v-lazy>
          <MiniArtifact
            v-if="isSideBar"
            :artifact-id="artifact.artifactId"
          />
          <Artifact
            v-else
            :artifact-id="artifact.artifactId"
          />
        </v-lazy>
      </v-col>
    </v-row>
  </v-container>
</template>
